import { S } from '../app/app_state.js?v=20260817c';
import { render2D } from './plan_renderer.js?v=20260904f';
import { withPlanCaptureTarget, PLAN_CAPTURE_MAX_NODES } from './plan_capture_context.mjs';
import { withPlanCaptureState } from './plan_capture_state.mjs';
import { releaseImportedSvgCaptureControllers } from '../objects/imported_svg_viewport_runtime.js';
import { setPrintPlanCaptureInProgress, restorePrintPlanCaptureInProgress, setPrintPlanSelectedGroupKeys, restorePrintPlanSelectedGroupKeys } from '../print/print_plan_context.js?v=20260826b';
import { snapshotBomPlanSystemSelections, setBomPlanSystemSelections, setBomPlanCaptureInProgress, restoreBomPlanCaptureInProgress } from '../bom/bom_plan_context.js?v=20260904c';

const MAX_NODES = PLAN_CAPTURE_MAX_NODES;
const MAX_MARKUP_BYTES = 32 * 1024 * 1024;
const sync = result => {
  if (result?.then) { Promise.resolve(result).catch(() => undefined); throw new TypeError('SVG capture hooks must be synchronous'); }
};

/** One temporary output tree, one active-project copy, no clone of the live plan. */
export function capturePlanSvg(options = {}) {
  const size = options.size || { w: 1000, h: 700 };
  const heapSamples = [];
  const sampleHeap = stage => {
    const bytes = globalThis.performance?.memory?.usedJSHeapSize;
    if (Number.isFinite(bytes)) heapSamples.push({ stage, usedJSHeapBytes: bytes });
  };
  sampleHeap('before-capture');
  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  host.style.cssText = `position:fixed;left:-100000px;top:0;width:${size.w}px;height:${size.h}px;visibility:hidden;pointer-events:none;contain:strict;`;
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  svg.setAttribute('width', String(size.w)); svg.setAttribute('height', String(size.h));
  svg.setAttribute('data-plan-output', 'true');
  host.appendChild(svg);
  return withPlanCaptureTarget(svg, size, () => {
    const previousBom = snapshotBomPlanSystemSelections();
    const previousPrintFlag = setPrintPlanCaptureInProgress(true);
    const previousBomFlag = setBomPlanCaptureInProgress(options.bomSelections != null);
    const explicitGroups = Object.hasOwn(options, 'selectedGroupKeys');
    const previousGroups = explicitGroups ? setPrintPlanSelectedGroupKeys(options.selectedGroupKeys, { notify: false }) : null;
    try {
      setBomPlanSystemSelections(options.bomSelections || null);
      document.body.appendChild(host);
      return withPlanCaptureState(S, options, () => {
        sampleHeap('after-project-copy');
        sync(options.prepare?.());
        render2D();
        sampleHeap('after-render');
        sync(options.filter?.(svg));
        svg.querySelectorAll('foreignObject').forEach(node => node.remove());
        const walker = document.createTreeWalker(svg, 1);
        let nodes = 0;
        while (walker.nextNode()) if (++nodes > MAX_NODES) throw new RangeError('Plan export exceeds SVG node budget');
        const markup = options.format === 'svg' ? svg.outerHTML : svg.innerHTML;
        const estimatedMarkupBytes = markup.length * 2;
        sampleHeap('after-serialization');
        if (estimatedMarkupBytes > MAX_MARKUP_BYTES) throw new RangeError('Plan export exceeds SVG markup budget');
        return { markup, camera: { ...S.camera2D }, size: { ...size },
          diagnostics: { nodes, estimatedMarkupBytes, maxNodes: MAX_NODES, maxMarkupBytes: MAX_MARKUP_BYTES,
            heapSamples, observedJsHeapMaxBytes: heapSamples.length ? Math.max(...heapSamples.map(sample => sample.usedJSHeapBytes)) : null,
            memoryScope: 'Optional coarse JS-heap phase samples; not peak DOM/native memory or a driver-memory measurement',
            source: 'separate-svg-compatibility-render', liveSvgCloned: false } };
      });
    } finally {
      try { releaseImportedSvgCaptureControllers(); }
      finally {
        host.remove(); svg.replaceChildren();
        setBomPlanSystemSelections(previousBom);
        if (explicitGroups) restorePrintPlanSelectedGroupKeys(previousGroups, { notify: false });
        restorePrintPlanCaptureInProgress(previousPrintFlag);
        restoreBomPlanCaptureInProgress(previousBomFlag);
      }
    }
  });
}
